import axios from 'axios';
import { MODULES } from 'figshelf-modules';

const API_BASE_URL = MODULES.Constants.constants.API_URL;

const unwrap = (response) => {
  const data = response?.data;
  if (data && typeof data === 'object' && Object.prototype.hasOwnProperty.call(data, 'data')) {
    return data.data;
  }
  return data;
};

class MessageService {
  constructor() {
    this.api = axios.create({
      baseURL: `${API_BASE_URL}/messages`,
      headers: {
        'Content-Type': 'application/json',
      },
    });

    // Add auth token to requests
    this.api.interceptors.request.use((config) => {
      const token = localStorage.getItem('@auth_token');
      if (token) {
        config.headers.Authorization = `Bearer ${token}`;
      }
      return config;
    });
  }

  async setTyping(conversationId, obj = {}) {
    console.log("Typing", conversationId, obj)
  }

  /**
   * Get user's conversations (inbox)
   */
  async getConversations(page = 1, limit = 20) {
    try {
      const response = await this.api.get('/', {
        params: { page, limit }
      });
      
      const responseData = response.data?.data || [];
      const meta = response.data?.meta || {};
      
      return {
        conversations: Array.isArray(responseData) ? responseData : [],
        total: meta.total || 0,
        page: meta.page || page,
        limit: meta.limit || limit,
        totalPages: meta.totalPages || Math.ceil((meta.total || 0) / (meta.limit || limit))
      };
    } catch (error) {
      console.error('Error fetching conversations:', error);
      throw error;
    }
  }

  /**
   * Get a single conversation with messages
   */
  async getConversation(conversationId, page = 1, limit = 50, config = {}) {
    try {
      const response = await this.api.get(`/${conversationId}`, {
        params: { page, limit },
        ...(config && typeof config === 'object' ? config : {}),
      });
      
      const rawData = response.data?.data || response.data;
      const meta = response.data?.meta || {};
      
      return {
        ...rawData,
        messages: Array.isArray(rawData.messages) ? rawData.messages : [],
        total: meta.total || 0,
        page: meta.page || page,
        limit: meta.limit || limit,
        totalPages: meta.totalPages || Math.ceil((meta.total || 0) / (meta.limit || limit))
      };
    } catch (error) {
      const isCanceled =
        error?.code === 'ERR_CANCELED' ||
        error?.name === 'CanceledError' ||
        (typeof axios.isCancel === 'function' && axios.isCancel(error));

      if (!isCanceled) {
        console.error('Error fetching conversation:', error);
      }
      throw error;
    }
  }

  /**
   * Create a new conversation
   */
  async createConversation(participants, name = null, type = 'direct', options = {}) {
    try {
      const payload = {
        participants,
        name,
        type,
        ...options,
      };
      
      const response = await this.api.post('/', payload);
      return unwrap(response);
    } catch (error) {
      console.error('Error creating conversation:', error);
      throw error;
    }
  }

  /**
   * Send a message
   */
  async sendMessage(conversationId, content, type = 'text', metadata = {}, replyTo = null) {
    try {
      const payload = {
        conversationId,
        type,
        metadata: Object.keys(metadata).length > 0 ? metadata : undefined,
        replyTo,
      };
      
      // Different message types expect different field names
      if (type === 'emoji') {
        payload.emoji = content;
      } else if (type === 'image' || type === 'gif' || type === 'voice') {
        payload.url = content;
        if (type === 'gif' && metadata.width && metadata.height) {
          payload.width = metadata.width;
          payload.height = metadata.height;
        }
        if (type === 'voice' && metadata.duration) {
          payload.duration = metadata.duration;
        }
      } else {
        payload.content = content;
      }
      
      const response = await this.api.post(`/${conversationId}`, payload);

      return unwrap(response);
    } catch (error) {
      console.error('Error sending message:', error);
      throw error;
    }
  }

  /**
   * Send a media message
   */
  async sendMediaMessage(conversationId, file, caption = '', type = 'image') {
    try {
      const formData = new FormData();
      formData.append('file', file);
      if (caption) {
        formData.append('caption', caption);
      }

      const response = await this.api.post(`/conversations/${conversationId}/messages/media`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
      
      return unwrap(response);
    } catch (error) {
      console.error('Error sending media message:', error);
      throw error;
    }
  }

  /**
   * Mark message as read
   */
  async markAsRead(conversationId, messageId) {
    try {
      const response = await this.api.put(`/${conversationId}/${messageId}/read`);
      return unwrap(response);
    } catch (error) {
      console.error('Error marking message as read:', error);
      throw error;
    }
  }

  /**
   * Mark all messages in conversation as read
   */
  async markAllAsRead(conversationId) {
    try {
      const response = await this.api.put(`/conversations/${conversationId}/read-all`);
      return unwrap(response);
    } catch (error) {
      console.error('Error marking all as read:', error);
      throw error;
    }
  }

  /**
   * Search messages
   */
  async searchMessages(conversationId, query, page = 1, limit = 20) {
    try {
      const response = await this.api.get(`/conversations/${conversationId}/search`, {
        params: { query, page, limit }
      });
      
      return unwrap(response);
    } catch (error) {
      console.error('Error searching messages:', error);
      throw error;
    }
  }

  /**
   * Mute conversation
   */
  async muteConversation(conversationId, duration = '1d') {
    try {
      const response = await this.api.post(`/conversations/${conversationId}/mute`, { duration });
      return unwrap(response);
    } catch (error) {
      console.error('Error muting conversation:', error);
      throw error;
    }
  }

  /**
   * Unmute conversation
   */
  async unmuteConversation(conversationId) {
    try {
      const response = await this.api.post(`/conversations/${conversationId}/unmute`);
      return unwrap(response);
    } catch (error) {
      console.error('Error unmuting conversation:', error);
      throw error;
    }
  }

  /**
   * Delete conversation
   */
  async deleteConversation(conversationId, deleteType = 'for_me', reason = '') {
    try {
      const response = await this.api.delete(`/conversations/${conversationId}`, {
        data: { deleteType, reason }
      });
      return unwrap(response);
    } catch (error) {
      console.error('Error deleting conversation:', error);
      throw error;
    }
  }

  /**
   * Archive conversation
   */
  async archiveConversation(conversationId, archive = true) {
    try {
      const response = await this.api.post(`/conversations/${conversationId}/archive`, { archive });
      return unwrap(response);
    } catch (error) {
      console.error('Error archiving conversation:', error);
      throw error;
    }
  }

  /**
   * Get archived conversations
   */
  async getArchivedConversations(page = 1, limit = 20) {
    try {
      const response = await this.api.get('/archived', {
        params: { page, limit }
      });
      return unwrap(response);
    } catch (error) {
      console.error('Error fetching archived conversations:', error);
      throw error;
    }
  }

  /**
   * Delete message
   */
  async deleteMessage(messageId, deleteType = 'for_me', reason = '') {
    try {
      const response = await this.api.delete(`/messages/${messageId}`, {
        data: { deleteType, reason }
      });
      return unwrap(response);
    } catch (error) {
      console.error('Error deleting message:', error);
      throw error;
    }
  }

  /**
   * Edit message
   */
  async editMessage(messageId, content) {
    try {
      const response = await this.api.put(`/messages/${messageId}`, { content });
      return unwrap(response);
    } catch (error) {
      console.error('Error editing message:', error);
      throw error;
    }
  }

  /**
   * Add reaction to message
   */
  async addReaction(messageId, emoji, conversationId) {
    try {
      console.log('[MessageService] addReaction', { conversationId, messageId, emoji });
      const response = await this.api.post(`/${conversationId}/messages/${messageId}/reactions`, { emoji });
      return unwrap(response);
    } catch (error) {
      console.error('Error adding reaction:', error);
      throw error;
    }
  }

  /**
   * Remove reaction from message
   */
  async removeReaction(messageId, emoji, conversationId) {
    try {
      console.log('[MessageService] removeReaction', { conversationId, messageId, emoji });
      const response = await this.api.delete(`/${conversationId}/messages/${messageId}/reactions`, {
        params: { emoji }
      });
      return unwrap(response);
    } catch (error) {
      console.error('Error removing reaction:', error);
      throw error;
    }
  }
}

const messageService = new MessageService();
export default messageService;
